import { useState, useEffect, useCallback } from 'react';
import {
  apiListProducts,
  apiGetProduct,
  categoryNameFromId,
  hashId,
  type ApiListItem,
  type ApiProductDetail,
} from '@/lib/api';

// These interfaces are unchanged from the original Readdy app EXCEPT that ids are
// strings (the template/Square uses string SKU ids, not Supabase serials). The
// pages consume these shapes verbatim, so the data layer swap is invisible to them.
export interface Product {
  id: string;
  name: string;
  category_id: number | null;
  status: string;
  description: string | null;
  price: number;
  stock: number;
  discount_enabled: boolean;
  discount_price: number | null;
  media: { url: string; type: string; alt?: string }[];
  product_categories?: { id: number; name: string } | null;
  pricing_mode?: number;
  hasVariants?: boolean;
}

export interface ProductVariant {
  id: number;
  product_id: string;
  name: string;
  options: string[];
  sort_order: number;
}

export interface ProductSku {
  id: string;
  product_id: string;
  label: string;
  options: string[];
  price: number;
  stock: number;
  discount_enabled: boolean;
  discount_price: number | null;
}

export interface ProductDetail {
  product: Product | null;
  variants: ProductVariant[];
  skus: ProductSku[];
}

// ---- mappers (template API shape -> the interfaces above) ----

function buildMedia(image: string, gallery: string[], alt: string): Product['media'] {
  const media: Product['media'] = [];
  const seen = new Set<string>();
  const push = (url: string, a: string) => {
    if (url && !seen.has(url)) { seen.add(url); media.push({ url, type: 'image', alt: a }); }
  };
  push(image, alt);
  (gallery || []).forEach((u) => push(u, alt));
  return media;
}

function mapListItem(it: ApiListItem): Product {
  return {
    id: it.id,
    name: it.name,
    category_id: it.category ? hashId(it.category) : null,
    status: 'active',
    description: it.description,
    price: it.price,
    stock: it.stock,
    discount_enabled: false,
    discount_price: null,
    media: buildMedia(it.image, it.gallery, it.name),
    product_categories: it.category ? { id: hashId(it.category), name: it.category } : null,
    hasVariants: it.hasVariants,
  };
}

// Detail media includes each variant's own image (alt = its option values) so the
// product page's image auto-switch (which matches media.alt to the selected option)
// works exactly as before.
function mapDetail(d: ApiProductDetail): Product {
  const media: Product['media'] = [];
  const seen = new Set<string>();
  const push = (url: string, alt: string) => {
    if (url && !seen.has(url)) { seen.add(url); media.push({ url, type: 'image', alt }); }
  };
  push(d.image, d.name);
  (d.gallery || []).forEach((u) => push(u, d.name));
  (d.variants || []).forEach((v) => {
    const alt = v.options.map((o) => o.value).join(' ') || v.optionLabel || d.name;
    push(v.image, alt);
  });

  // Variant parents return price null; show the cheapest child ("from $X").
  const childPrices = (d.variants || []).map((v) => v.price).filter((n) => n > 0);
  const price = d.hasVariants && childPrices.length ? Math.min(...childPrices) : (d.price ?? 0);

  return {
    id: d.id,
    name: d.name,
    category_id: d.category ? hashId(d.category) : null,
    status: 'active',
    description: d.description,
    price: Number.isFinite(price) ? price : (d.price ?? 0),
    stock: 0,
    discount_enabled: false,
    discount_price: null,
    media,
    product_categories: d.category ? { id: hashId(d.category), name: d.category } : null,
    hasVariants: d.hasVariants,
  };
}

// Build the Readdy variant model (dimensions + skus) from the template's flat list
// of variant children. Each child = one ProductSku; dimensions are grouped from the
// children's structured options (Size / Style / Color), or a single "Option"
// dimension built from the variant labels when there are no structured options.
function buildVariantModel(d: ApiProductDetail): { variants: ProductVariant[]; skus: ProductSku[] } {
  if (!d.hasVariants || !(d.variants || []).length) return { variants: [], skus: [] };

  const skus: ProductSku[] = d.variants.map((v) => ({
    id: v.id,
    product_id: d.id,
    label: v.optionLabel || v.options.map((o) => o.value).join(' / ') || '',
    options: v.options.length ? v.options.map((o) => o.value) : (v.optionLabel ? [v.optionLabel] : []),
    price: v.price,
    stock: v.stock,
    discount_enabled: false,
    discount_price: null,
  }));

  const structured = d.variants.some((v) => v.options.length > 0);
  let variants: ProductVariant[];
  if (structured) {
    const order: string[] = [];
    const values: Record<string, string[]> = {};
    for (const v of d.variants) {
      for (const o of v.options) {
        if (!order.includes(o.name)) { order.push(o.name); values[o.name] = []; }
        if (!values[o.name].includes(o.value)) values[o.name].push(o.value);
      }
    }
    variants = order.map((name, i) => ({ id: i + 1, product_id: d.id, name, options: values[name], sort_order: i }));
  } else {
    variants = [{ id: 1, product_id: d.id, name: 'Option', options: skus.map((s) => s.label).filter(Boolean), sort_order: 0 }];
  }
  return { variants, skus };
}

function sortProducts(list: Product[], sort?: string): Product[] {
  const a = [...list];
  if (sort === 'price-asc') a.sort((x, y) => x.price - y.price);
  else if (sort === 'price-desc') a.sort((x, y) => y.price - x.price);
  else if (sort === 'name-asc') a.sort((x, y) => x.name.localeCompare(y.name));
  // 'newest' / 'default' -> keep server order (sort, updated_at).
  return a;
}

export function useProducts() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProducts = useCallback(async (category?: number | string, search?: string, sort?: string) => {
    setLoading(true);
    setError(null);
    try {
      let name: string | undefined;
      if (typeof category === 'number') name = await categoryNameFromId(category);
      else if (typeof category === 'string') name = category;

      const items = await apiListProducts(name, search && search.trim() ? search.trim() : undefined);
      setProducts(sortProducts(items.map(mapListItem), sort));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load products');
    } finally {
      setLoading(false);
    }
  }, []);

  return { products, loading, error, fetchProducts };
}

export function useProduct(id: number | string | undefined) {
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProduct = useCallback(async (productId: number | string) => {
    setLoading(true);
    setError(null);
    try {
      const d = await apiGetProduct(String(productId));
      if (!d || d.error) setProduct(null);
      else setProduct(mapDetail(d));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load product');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (id !== undefined && id !== null && id !== '') fetchProduct(id);
  }, [id, fetchProduct]);

  return { product, loading, error, refetch: () => (id != null ? fetchProduct(id) : undefined) };
}

export function useProductDetails(id: number | string | undefined) {
  const [detail, setDetail] = useState<ProductDetail>({ product: null, variants: [], skus: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDetails = useCallback(async (productId: number | string) => {
    setLoading(true);
    setError(null);
    try {
      const d = await apiGetProduct(String(productId));
      if (!d || d.error) {
        setDetail({ product: null, variants: [], skus: [] });
        setError('not_found');
      } else {
        const { variants, skus } = buildVariantModel(d);
        setDetail({ product: mapDetail(d), variants, skus });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load product');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (id !== undefined && id !== null && id !== '') fetchDetails(id);
  }, [id, fetchDetails]);

  return { detail, loading, error, refetch: () => (id != null ? fetchDetails(id) : undefined) };
}
